import React from 'react';
import styles from './M_SearchItem.module.css';
import A_UserAvatar from '@/components/A_Atoms/A_UserAvatar/A_UserAvatar';
import { User } from '@/types';
import cn from 'classnames';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import { useIsMobile } from '@/hooks/useIsMobile';

interface M_SearchItemProps {
  user: User;
  query: string;
  onClick?: () => void;
}

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getHighlightedContent = (
  text: string,
  query: string
): React.ReactNode => {
  if (!query) {
    return text;
  }

  const regex = new RegExp(`(${escapeRegExp(query)})`, 'ig');
  const parts = text.split(regex);

  if (parts.length === 1) {
    return text;
  }

  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <span key={`${part}-${index}`} className={styles.highlight}>
        {part}
      </span>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    )
  );
};

export default function M_SearchItem({
  user,
  query,
  onClick,
}: M_SearchItemProps) {
  const { language } = useLanguage();
  const isMobile = useIsMobile(1080);
  const t = (key: string) => getTranslation(language, key);
  const normalizedQuery = query.trim();

  const formatDateOfBirth = (dateString?: string) => {
    if (!dateString) return t('userProfileCard.notSpecified');

    try {
      const date = new Date(dateString);
      const locale = language === 'ru' ? 'ru-RU' : 'en-US';
      return date.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const firstName = user.personal.firstName;
  const lastName = user.personal.lastName;
  const userName =
    firstName || lastName
      ? `${firstName ?? ''} ${lastName ?? ''}`.trim()
      : user.handle;

  const city =
    user.location.residencePlace?.city ||
    user.location.label?.split(',')[0] ||
    t('userProfileCard.notSpecified');

  const avatar = user.avatar;
  const birthDate = formatDateOfBirth(user.personal.dateOfBirth);
  const handle = user.handle;
  const highlightedUserName = getHighlightedContent(userName, normalizedQuery);
  const highlightedHandle = handle
    ? getHighlightedContent(`@${handle}`, normalizedQuery)
    : null;
  const highlightedCity = getHighlightedContent(city, normalizedQuery);
  const highlightedBirthDate = getHighlightedContent(
    birthDate,
    normalizedQuery
  );

  const handleButtonClick = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    onClick?.();
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.user_info_wrapper}>
        <div className={styles.avatar_wrapper}>
          {avatar ? (
            <img src={avatar} alt={userName} className={styles.avatar} />
          ) : (
            <A_UserAvatar width={28} height={28} />
          )}
        </div>
        <div
          className={cn(styles.user_info_column, {
            [styles.mobile_column]: isMobile,
          })}
        >
          <span className={cn(styles.user_name, 'text-p-7')}>
            {highlightedUserName}
          </span>
          {highlightedHandle && (
            <span className={cn(styles.user_handle, 'text-p-7')}>
              {highlightedHandle}
            </span>
          )}
        </div>
      </div>
      {!isMobile && (
        <div className={styles.details}>
          <span className='text-p-7'>{highlightedCity}</span>
          <span className='text-p-7'>{highlightedBirthDate}</span>
        </div>
      )}
      <div
        className={cn(styles.button_wrapper, {
          [styles.mobile_visible]: isMobile,
        })}
      >
        <A_Button
          variant={isMobile ? 'secondary' : 'primary'}
          size='small'
          fontSize='text-p-4'
          onClick={handleButtonClick}
        >
          {t('inviteCoauthors.invite')}
        </A_Button>
      </div>
    </div>
  );
}
